import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Sparkles, Check, X, Bot, UserCheck, FileWarning, Maximize2, ScanFace } from "lucide-react";

const DOC_ORDER = [
  ["selfie", "Selfie photo"],
  ["gov_id", "Government ID"],
  ["bank_statement", "Bank statement"],
];

const STATUS_STYLE = {
  approved: ["Approved", "bg-green-600 text-white"],
  rejected: ["Rejected", "bg-red-500 text-white"],
  pending: ["Pending", "bg-amber-500 text-white"],
  missing: ["Not uploaded", "bg-muted text-muted-foreground"],
};

function StatusBadge({ status }) {
  const [label, cls] = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return <Badge className={cls}>{label}</Badge>;
}

const isImage = (u) => /\.(png|jpe?g|webp|gif|heic|heif)(\?|$)/i.test(u || "") || (u || "").startsWith("data:image");

function DocCard({ uid, docKey, label, doc, onDecide, busyKey }) {
  const url = doc?.url;
  const ai = doc?.ai;
  return (
    <div className="rounded-xl border p-3 flex flex-col gap-2" data-testid={`verify-doc-${docKey}-${uid}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">{label}</p>
        <StatusBadge status={doc?.status || "missing"} />
      </div>

      {!doc?.uploaded ? (
        <div className="p-4 rounded-lg border border-dashed flex items-center gap-2 text-sm text-muted-foreground">
          <FileWarning className="w-4 h-4" /> Not uploaded
        </div>
      ) : (
        <>
          {isImage(url) ? (
            <button type="button" onClick={() => window.open(url, "_blank", "noopener")} className="block w-full group relative">
              <img src={url} alt={label} className="max-h-52 w-full object-contain rounded-lg bg-muted cursor-zoom-in" />
              <span className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Maximize2 className="w-3.5 h-3.5" /></span>
            </button>
          ) : (
            <a href={url} target="_blank" rel="noreferrer noopener" className="text-sm text-primary underline">Open document ↗</a>
          )}

          {ai && (
            <div className={`text-xs rounded-lg px-2.5 py-2 flex items-start gap-1.5 ${ai.verdict === "pass" ? "bg-green-50 text-green-800" : ai.verdict === "fail" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
              <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span><span className="font-semibold uppercase">{ai.verdict}</span>{ai.reason ? ` — ${ai.reason}` : ""}</span>
            </div>
          )}
          {doc?.reason && doc?.reviewed_by && doc?.reviewed_by !== "ai" && (
            <p className="text-xs text-muted-foreground">Note: {doc.reason}</p>
          )}

          <div className="flex gap-2 mt-1">
            <Button data-testid={`approve-doc-${docKey}-${uid}`} size="sm" variant="outline" className="flex-1 border-green-600 text-green-700 hover:bg-green-50"
              disabled={doc?.status === "approved" || busyKey === uid + docKey + "approve"} onClick={() => onDecide(uid, docKey, "approve")}>
              {busyKey === uid + docKey + "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Approve
            </Button>
            <Button data-testid={`reject-doc-${docKey}-${uid}`} size="sm" variant="outline" className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
              disabled={busyKey === uid + docKey + "reject"} onClick={() => onDecide(uid, docKey, "reject")}>
              {busyKey === uid + docKey + "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5 mr-1" />} Reject
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function SellerVerificationManager() {
  const [data, setData] = useState({ mode: "manual", sellers: [] });
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/verifications");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load verifications");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setMode = async (mode) => {
    try {
      await api.put("/admin/finance/settings", { verification_mode: mode });
      setData((d) => ({ ...d, mode }));
      toast.success(mode === "automatic" ? "Automatic AI verification enabled" : "Manual review mode enabled");
    } catch (e) { toast.error("Failed to update mode"); }
  };

  const decide = async (uid, doc, decision) => {
    let reason = "";
    if (decision === "reject") reason = window.prompt("Reason for rejecting this document (optional):") || "";
    setBusyKey(uid + doc + decision);
    try {
      await api.post(`/admin/verifications/${uid}/verify-doc`, { doc, decision, reason });
      toast.success(`Document ${decision === "approve" ? "approved" : "rejected"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusyKey(""); }
  };

  const runAi = async (uid) => {
    setBusyKey(uid + "ai");
    try {
      await api.post(`/admin/verifications/${uid}/auto-verify`);
      toast.success("AI check complete");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI check failed"); }
    finally { setBusyKey(""); }
  };

  const sellerAction = async (uid, action) => {
    let reason = "";
    if (action === "reject") {
      reason = (window.prompt("Reason for rejecting this seller (required — the applicant will be emailed this):") || "").trim();
      if (!reason) { toast.error("A rejection reason is required."); return; }
    }
    setBusyKey(uid + action);
    try {
      const url = action === "reject"
        ? `/admin/sellers/${uid}/reject?reason=${encodeURIComponent(reason)}`
        : `/admin/sellers/${uid}/${action}`;
      await api.post(url);
      toast.success(`Seller ${action}d — applicant notified by email`);
      await load();
    } catch (e) { toast.error("Action failed"); }
    finally { setBusyKey(""); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const pendingCount = data.sellers.filter((s) => s.seller_status === "pending").length;
  const visibleSellers = filter === "pending"
    ? data.sellers.filter((s) => s.seller_status === "pending")
    : data.sellers;

  return (
    <div className="space-y-5">
      <Card className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <p className="font-semibold text-sm">Approval mode</p>
            <p className="text-xs text-muted-foreground max-w-md">
              {data.mode === "automatic"
                ? "New sellers' documents are auto-checked by AI on submission. Clear passes auto-approve; anything unclear is flagged for your manual review."
                : "You review and approve each seller's documents manually. You can still run an AI check on demand."}
            </p>
          </div>
        </div>
        <div className="inline-flex rounded-full border p-1 bg-muted/40 shrink-0" data-testid="verification-mode-toggle">
          <button data-testid="mode-manual" onClick={() => setMode("manual")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${data.mode !== "automatic" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}>
            <UserCheck className="w-4 h-4" /> Manual
          </button>
          <button data-testid="mode-automatic" onClick={() => setMode("automatic")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${data.mode === "automatic" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}>
            <Sparkles className="w-4 h-4" /> Automatic (AI)
          </button>
        </div>
      </Card>

      <div className="inline-flex rounded-full border p-1 bg-muted/40" data-testid="verification-filter-toggle">
        <button data-testid="filter-pending" onClick={() => setFilter("pending")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${filter === "pending" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}>
          Pending review
          {pendingCount > 0 && <Badge className="bg-amber-500 text-white ml-1">{pendingCount}</Badge>}
        </button>
        <button data-testid="filter-all" onClick={() => setFilter("all")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === "all" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}>
          All sellers
        </button>
      </div>

      {visibleSellers.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground text-sm" data-testid="no-verifications">
          {filter === "pending" ? "No seller applications awaiting review right now. 🎉" : "No seller documents on file yet."}
        </Card>
      )}

      {visibleSellers.map((s) => (
        <Card key={s.user_id} className="p-4 space-y-3" data-testid={`verification-seller-${s.user_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-semibold">{s.business_name}</p>
              <p className="text-xs text-muted-foreground">{s.email}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="capitalize">{s.verification_state === "no_docs" ? "No docs" : s.verification_state}</Badge>
              {s.face_match && (
                <Badge data-testid={`face-match-${s.user_id}`} className={`gap-1 ${s.face_match.verdict === "pass" ? "bg-green-600 text-white" : s.face_match.verdict === "fail" ? "bg-red-500 text-white" : "bg-amber-500 text-white"}`} title={s.face_match.reason || ""}>
                  <ScanFace className="w-3.5 h-3.5" /> Face {s.face_match.verdict}
                </Badge>
              )}
              <Badge className={s.seller_status === "approved" ? "bg-green-600 text-white" : s.seller_status === "pending" ? "bg-amber-500 text-white" : "bg-red-500 text-white"}>{s.seller_status}</Badge>
              <Button data-testid={`run-ai-${s.user_id}`} size="sm" variant="secondary" disabled={busyKey === s.user_id + "ai"} onClick={() => runAi(s.user_id)}>
                {busyKey === s.user_id + "ai" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Run AI check
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {DOC_ORDER.map(([k, label]) => (
              <DocCard key={k} uid={s.user_id} docKey={k} label={label} doc={s.docs?.[k]} onDecide={decide} busyKey={busyKey} />
            ))}
          </div>

          <div className="flex gap-2 flex-wrap pt-1 border-t">
            <Button data-testid={`verify-approve-seller-${s.user_id}`} size="sm" className="bg-green-600 hover:bg-green-700 text-white"
              disabled={s.seller_status === "approved" || busyKey === s.user_id + "approve"} onClick={() => sellerAction(s.user_id, "approve")}>
              <UserCheck className="w-3.5 h-3.5 mr-1" /> Approve seller
            </Button>
            <Button data-testid={`verify-reject-seller-${s.user_id}`} size="sm" variant="outline" className="border-red-500 text-red-600 hover:bg-red-50"
              disabled={busyKey === s.user_id + "reject"} onClick={() => sellerAction(s.user_id, "reject")}>
              Reject seller
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
